import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Field,
  Form,
  Modal,
  ModalProps,
  RawInput,
} from '@payaca/ambrosia';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod/v4';

import { TProposalAttachment } from '@/api/queries/proposal/useGetProposal';

const renameAttachmentSchema = z.object({
  fileName: z.string().trim().min(1, 'Enter a file name'),
});

type TFormValues = z.infer<typeof renameAttachmentSchema>;

const RenameAttachmentModal: FC<
  {
    attachmentToRename?: TProposalAttachment;
    /** Called with the new file name when the form is submitted. */
    onSave: (fileName: string) => void;
    isProcessing?: boolean;
  } & Omit<ModalProps, 'title'>
> = (props) => {
  const { attachmentToRename, onSave, isProcessing, onClose, ...rest } = props;

  const formMethods = useForm<TFormValues>({
    defaultValues: { fileName: attachmentToRename?.fileName ?? '' },
    resolver: zodResolver(renameAttachmentSchema),
  });

  const {
    register,
    reset,
    formState: { isDirty, isValid },
  } = formMethods;

  // Reset the form when a different attachment is opened — the Modal stays
  // mounted (for its close animation), so defaultValues alone won't refresh.
  useEffect(() => {
    if (attachmentToRename) {
      reset({ fileName: attachmentToRename.fileName });
    }
  }, [attachmentToRename, reset]);

  const handleSubmit = (values: TFormValues) => {
    onSave(values.fileName);
  };

  return (
    <Modal title="Rename attachment" onClose={onClose} {...rest}>
      <Form methods={formMethods} onSubmit={handleSubmit}>
        <Modal.Body>
          <Field name="fileName">
            <Field.Label>File name</Field.Label>
            <RawInput {...register('fileName')} />
          </Field>
        </Modal.Body>

        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isProcessing={isProcessing}
              disabled={isProcessing || !isDirty}
            >
              Save
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default RenameAttachmentModal;
